// typeFormHelpers — pure builders / parsers for TypeEditFormBody.
//
// Extracted into a sibling non-TSX module to (1) keep the form component
// under the 200-line budget per CLAUDE.md, and (2) avoid the
// react-refresh/only-export-components lint trap that fires when a TSX file
// exports both a component and runtime helpers.
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice D4 — Type / Domain UI.

import type { QualifiedName, SqlEngine } from '../../../../sql-grammar/model';
import type {
  UserDefinedTypeDef,
  UserDefinedTypeKind,
} from '../../../../sql-resolver/types/ddlEdit';

export interface TypeFormValues {
  /** Bare or schema-qualified type name. */
  readonly name: string;
  /** Discriminator across UserDefinedTypeDef arms. */
  readonly kind: UserDefinedTypeKind;
  /** Comma-separated enum values (pgsql enum). */
  readonly enumValues?: string;
  /** Newline-separated `name TYPE` attributes (composite / object). */
  readonly attributes?: string;
  /** Verbatim base type for alias / domain / table-of / varray. */
  readonly baseType?: string;
  /** pgsql range subtype. */
  readonly subtype?: string;
  /** pgsql domain `DEFAULT <expr>` (verbatim). */
  readonly defaultText?: string;
  /** pgsql domain NOT NULL flag. */
  readonly notNull?: boolean;
  /** pgsql domain CHECK expressions, one per line. */
  readonly checkExpressions?: string;
  /** Oracle CREATE OR REPLACE flag. */
  readonly orReplace?: boolean;
  /** Oracle VARRAY size. */
  readonly varraySize?: string;
}

export function defaultKindForEngine(engine: SqlEngine): UserDefinedTypeKind {
  if (engine === 'mssql') return 'alias';
  if (engine === 'postgres') return 'enum';
  if (engine === 'oracle') return 'object';
  return 'alias';
}

export function parseCommaList(input: string | undefined): readonly string[] {
  if (!input) return [];
  return input
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export function parseLineList(input: string | undefined): readonly string[] {
  if (!input) return [];
  return input
    .split('\n')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export function parseAttributes(
  input: string | undefined,
): ReadonlyArray<{ readonly name: string; readonly typeRef: string }> {
  return parseLineList(input).map((line) => {
    const idx = line.search(/\s/);
    if (idx < 0) return { name: line, typeRef: '' };
    return {
      name: line.slice(0, idx).trim(),
      typeRef: line.slice(idx + 1).trim(),
    };
  });
}

export function buildUserDefinedTypeDef(
  values: TypeFormValues,
  engine: SqlEngine,
  fallbackName: QualifiedName,
): UserDefinedTypeDef {
  const trimmedName = values.name.trim();
  const name: QualifiedName = trimmedName === fallbackName.name
    ? fallbackName
    : {
        ...fallbackName,
        name: trimmedName,
        raw: trimmedName,
        foldedName: trimmedName.toLowerCase(),
      };
  switch (values.kind) {
    case 'alias':
      return { kind: 'alias', name, baseType: (values.baseType ?? '').trim() };
    case 'table':
      // mssql TVP — column list editing not yet exposed; the form rejects
      // before we get here. Surface an empty columns array which the
      // validator will catch.
      return { kind: 'table', name, columns: [] };
    case 'enum':
      return { kind: 'enum', name, values: parseCommaList(values.enumValues) };
    case 'composite':
      return {
        kind: 'composite',
        name,
        attributes: parseAttributes(values.attributes),
      };
    case 'domain': {
      const checks = parseLineList(values.checkExpressions).map(
        (expression) => ({ expression }),
      );
      return {
        kind: 'domain',
        name,
        baseType: (values.baseType ?? '').trim(),
        ...(values.defaultText && values.defaultText.trim().length > 0
          ? { defaultText: values.defaultText.trim() }
          : {}),
        ...(values.notNull ? { notNull: true } : {}),
        ...(checks.length > 0 ? { checks } : {}),
      };
    }
    case 'range':
      return {
        kind: 'range',
        name,
        subtype: (values.subtype ?? '').trim(),
      };
    case 'object':
      return {
        kind: 'object',
        name,
        attributes: parseAttributes(values.attributes),
        ...(values.orReplace && engine === 'oracle' ? { orReplace: true } : {}),
      };
    case 'table-of':
      return {
        kind: 'table-of',
        name,
        baseType: (values.baseType ?? '').trim(),
        ...(values.orReplace && engine === 'oracle' ? { orReplace: true } : {}),
      };
    case 'varray': {
      const size = Number((values.varraySize ?? '').trim());
      return {
        kind: 'varray',
        name,
        size: Number.isFinite(size) ? size : 0,
        baseType: (values.baseType ?? '').trim(),
        ...(values.orReplace && engine === 'oracle' ? { orReplace: true } : {}),
      };
    }
  }
}

export function describeKindLabel(kind: UserDefinedTypeKind): string {
  switch (kind) {
    case 'alias': return 'Alias (FROM)';
    case 'table': return 'Table-valued (TVP)';
    case 'enum': return 'Enum';
    case 'composite': return 'Composite';
    case 'domain': return 'Domain';
    case 'range': return 'Range';
    case 'object': return 'Object';
    case 'table-of': return 'Table OF';
    case 'varray': return 'VARRAY';
  }
}
